import React from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate } from 'react-router-dom';
import './App.css';
import API_BASE_URL from './config';
import PDLHealthRecord from './components/hr_info';
import HRWizard from './components/HRWizard';
import PdlHealthRecordSearch from './components/PdlHealthRecordSearch'; // New
import PdlConsultationSearch from './components/pdl_consultation_search'; // New
import SuJoConsultationSearch from './components/su_jo_search_consultation';
import FinalRemarks from './components/final_remarks'; // Final Remarks
import FinalRemarksSearch from './components/final_remarks_search'; // Final Remarks Search
import PdlCellUpdate from './components/pdl_cell_update'; // PDL Cell Update
import ConsultWizard from './components/consultwizard';
import ConsultSearch from './components/consult_seach';
import Login from './components/login';
import LoginAdmin from './components/login_admin';
import LoginSuperuser from './components/login_superuser';
import LoginJO from './components/login_jo';
import SuHomepage from './components/su_homepage'; // SU Homepage component
import SaHomepage from './components/sa_homepage'; // SA Homepage component
import JoHomepage from './components/jo_homepage'; // JO Homepage component
import JoPdlCell from './components/jo_pdl_cell'; // JO PDL Cell component
import SuActivityLogs from './components/su_activity_logs'; // SU Activity Logs
import ProtectedRoute from './components/ProtectedRoute'; // Route protection
import analyticsService from './services/analyticsService';
import SuJoHealthRecordSearch from './components/su_jo_search_healthrecord';
import JoSearchPdl from './components/jo_search_pdl';
import FilterPdl from './components/filter_pdl'; // Filter PDL by Health Condition
import NotificationBell from './components/notification'; // Notification Bell
import ChangePasswordModal from './components/change_pass'; // Change Password Modal
import authFetch from './utils/authFetch';
function FrontPage() {
  const navigate = useNavigate();
  const [currentTime, setCurrentTime] = React.useState(new Date());
  const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);
  const [userData, setUserData] = React.useState(null);
  const [isDarkMode, setIsDarkMode] = React.useState(() => {
    const saved = localStorage.getItem('theme');
    return saved === 'dark' || saved === null; // Default to dark mode
  });
  const [showThemeModal, setShowThemeModal] = React.useState(false);
  const [pendingTheme, setPendingTheme] = React.useState(null);
  const [showLogoutModal, setShowLogoutModal] = React.useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = React.useState(false);
  const [showSuccessModal, setShowSuccessModal] = React.useState(false);
  const [successMessage, setSuccessMessage] = React.useState('');
  const [showErrorModal, setShowErrorModal] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState('');
  
  // Analytics data state
  const [analyticsData, setAnalyticsData] = React.useState({
    kpis: null,
    demographics: null,
    ageDistribution: null,
    genderDistribution: null,
    activities: null,
    facility: null,
    healthRecordsCount: null,
    generalDormitoryCount: null,
    isolationCount: null,
    hospitalCount: null,
    dailyConsultationCount: null,
    unassignedPdlCount: null,
    cellDistribution: null
  });
  const [loading, setLoading] = React.useState(true);

  // Check authentication and load user data
  React.useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (!storedUser) {
      // No user data, redirect to login
      navigate('/login', { replace: true });
      return;
    }
    try {
      const parsedUser = JSON.parse(storedUser);
      setUserData(parsedUser);
    } catch (error) {
      console.error('Error parsing user data:', error);
      navigate('/login', { replace: true });
    }
  }, [navigate]);

  React.useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Load analytics data
  React.useEffect(() => {
    const loadAnalyticsData = async () => {
      try {
        setLoading(true);
        
        // Load core dashboard data first
        const dashboardData = await analyticsService.getDashboardData();
        const ageData = await analyticsService.getAgeDistribution();
        const genderData = await analyticsService.getGenderDistribution();
        
        // Set initial data
        setAnalyticsData(prev => ({
          ...prev,
          kpis: dashboardData.kpis,
          demographics: dashboardData.demographics,
          ageDistribution: ageData,
          genderDistribution: genderData,
          activities: dashboardData.activities,
          facility: dashboardData.facility
        }));
        
        // Load additional count data with individual error handling
        const loadAdditionalData = async () => {
          try {
            const healthRecordsCount = await analyticsService.getHealthRecordsCount();
            setAnalyticsData(prev => ({ ...prev, healthRecordsCount }));
          } catch (error) {
            console.warn('Failed to load health records count:', error);
          }
          
          try {
            const dailyConsultationCount = await analyticsService.getDailyConsultationCount();
            setAnalyticsData(prev => ({ ...prev, dailyConsultationCount }));
          } catch (error) {
            console.warn('Failed to load daily consultation count:', error);
          }
          
          try {
            const generalDormitoryCount = await analyticsService.getGeneralDormitoryCount();
            setAnalyticsData(prev => ({ ...prev, generalDormitoryCount }));
          } catch (error) {
            console.warn('Failed to load general dormitory count:', error);
          }
          
          try {
            const isolationCount = await analyticsService.getIsolationCount();
            setAnalyticsData(prev => ({ ...prev, isolationCount }));
          } catch (error) {
            console.warn('Failed to load isolation count:', error);
          }
          
          try {
            const hospitalCount = await analyticsService.getHospitalCount();
            setAnalyticsData(prev => ({ ...prev, hospitalCount }));
          } catch (error) {
            console.warn('Failed to load hospital count:', error);
          }

          try {
            const unassignedPdlCount = await analyticsService.getUnassignedPdlCount();
            setAnalyticsData(prev => ({ ...prev, unassignedPdlCount }));
          } catch (error) {
            console.warn('Failed to load unassigned PDLs count:', error);
          }

          try {
            const cellDistribution = await analyticsService.getCellDistribution();
            setAnalyticsData(prev => ({ ...prev, cellDistribution }));
          } catch (error) {
            console.warn('Failed to load cell distribution:', error);
          }
        };
        
        // Load additional data after core data is set
        await loadAdditionalData();
        
      } catch (error) {
        console.error('Failed to load core analytics data:', error);
        // Keep default values if API fails
      } finally {
        setLoading(false);
      }
    };

    loadAnalyticsData();
  }, []);

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleLogout = () => {
    setShowLogoutModal(true);
  };

  const confirmLogout = async () => {
    // Revoke token on backend
    try {
      await authFetch(`${API_BASE_URL}/api/auth/logout`, { method: 'POST' });
    } catch (e) { /* ignore - clearing token locally anyway */ }
    // Clear all stored authentication data
    localStorage.removeItem('user');
    localStorage.clear();
    sessionStorage.clear();
    setShowLogoutModal(false);
    // Use replace to prevent back button navigation
    navigate('/login', { replace: true });
    // Force page reload to clear any cached state
    window.location.href = '/login';
  };

  const cancelLogout = () => {
    setShowLogoutModal(false);
  };

  const toggleDarkMode = (newMode) => {
    setPendingTheme(newMode);
    setShowThemeModal(true);
  };

  const confirmThemeChange = async () => {
    // Close modal immediately
    setShowThemeModal(false);
    
    // Update local state and storage
    setIsDarkMode(pendingTheme);
    localStorage.setItem('theme', pendingTheme ? 'dark' : 'light');
    
    try {
      // Update database in background
      if (userData && userData.id) {
        const response = await authFetch(`${API_BASE_URL}/api/update-theme`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            user_id: userData.id,
            is_darkmode: pendingTheme ? 1 : 0
          })
        });

        if (!response.ok) {
          console.error('Failed to update theme in database');
        }
      }
    } catch (error) {
      console.error('Error updating theme:', error);
    } finally {
      setPendingTheme(null);
    }
  };

  const cancelThemeChange = () => {
    setShowThemeModal(false);
    setPendingTheme(null);
  };

  const formatDate = (date) => {
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    return date.toLocaleDateString('en-US', options);
  };

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (isDropdownOpen && !event.target.closest('.profile-section')) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  // Test function for debugging - accessible from browser console
  React.useEffect(() => {
    window.testAnalyticsAPI = async () => {
      try {
        await analyticsService.getKpis();
        await analyticsService.getHealthRecordsCount();
        await analyticsService.getDailyConsultationCount();
        await analyticsService.getGeneralDormitoryCount();
      } catch (error) {
        console.error('API test failed:', error);
      }
    };
  }, [analyticsData]);

  return (
    <div className={`system-app ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
      {/* System Header */}
      <header className="system-header">
        <div className="header-left">
          <div className="system-logo">
            <img src="/BJMPRO-CAR.png" alt="BJMP CAR Logo" className="system-logo-img" />
            <div className="system-title">
              <h1>MediCast: BJMP Health Monitoring System</h1>
              <p>Bureau of Jail Management and Penology</p>
            </div>
          </div>
        </div>
        <div className="header-right">
          <div className="system-info">
            <div className="system-time">{currentTime.toLocaleTimeString()}</div>
            <div className="system-user">{formatDate(currentTime)}</div>
          </div>
          <NotificationBell />
          <div className="profile-section">
            <div className="profile-icon" onClick={toggleDropdown}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 12C14.7614 12 17 9.76142 17 7C17 4.23858 14.7614 2 12 2C9.23858 2 7 4.23858 7 7C7 9.76142 9.23858 12 12 12Z" fill="currentColor"/>
                <path d="M12 14C7.58172 14 4 17.5817 4 22H20C20 17.5817 16 14 12 14Z" fill="currentColor"/>
              </svg>
            </div>
            {isDropdownOpen && (
              <div className="profile-dropdown">
                <div className="dropdown-header">
                  <div className="user-info">
                    <div className="user-role">
                      {userData?.user_type === 'unit_nurse' ? 'Unit Nurse' : 
                       userData?.user_type === 'chief_nurse' ? 'Chief Nurse' : 'User'}
                    </div>
                    <div className="user-name">
                      {userData ? 
                        `${userData.last_name || ''}, ${userData.first_name || ''} ${userData.middle_name || ''}`.trim() 
                        : 'User Name'}
                    </div>
                  </div>
                </div>
                <div className="dropdown-divider"></div>
                <button className="dropdown-item" onClick={() => toggleDarkMode(!isDarkMode)}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    {isDarkMode ? (
                      <path d="M12 3V4M12 20V21M4 12H3M6.31412 6.31412L5.5 5.5M17.6859 6.31412L18.5 5.5M6.31412 17.69L5.5 18.5M17.6859 17.69L18.5 18.5M21 12H20M16 12C16 14.2091 14.2091 16 12 16C9.79086 16 8 14.2091 8 12C8 9.79086 9.79086 8 12 8C14.2091 8 16 9.79086 16 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    ) : (
                      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" fill="currentColor" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    )}
                  </svg>
                  {isDarkMode ? 'Light Mode' : 'Dark Mode'}
                </button>
                <button className="dropdown-item" onClick={() => {
                  setShowChangePasswordModal(true);
                  setIsDropdownOpen(false);
                }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M7 11V7C7 5.67392 7.52678 4.40215 8.46447 3.46447C9.40215 2.52678 10.6739 2 12 2C13.3261 2 14.5979 2.52678 15.5355 3.46447C16.4732 4.40215 17 5.67392 17 7V11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Change Password
                </button>
                <button className="dropdown-item" onClick={handleLogout}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <polyline points="16,17 21,12 16,7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <line x1="21" y1="12" x2="9" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Dashboard Layout */}
      <div className="dashboard-container">
        {/* Sidebar Navigation */}
        <aside className="sidebar">
          <div className="sidebar-section">
            <div className="sidebar-title">Main Modules</div>
            <ul className="sidebar-menu">
              <li className="sidebar-item">
                <button type="button" className="sidebar-link active">
                  <svg className="sidebar-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3 12L5 10M5 10L12 3L19 10M5 10V20C5 20.5523 5.44772 21 6 21H9M19 10L21 12M19 10V20C19 20.5523 18.5523 21 18 21H15M9 21C9.55228 21 10 20.5523 10 20V16C10 15.4477 10.4477 15 11 15H13C13.5523 15 14 15.4477 14 16V20C14 20.5523 14.4477 21 15 21M9 21H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Dashboard
                </button>
              </li>
              <li className="sidebar-item">
                <button type="button" className="sidebar-link" onClick={() => navigate('/pdl-health-wizard')}>
                  <svg className="sidebar-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2V22M17 7H7M17 17H7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
                  </svg>
                  Health Records
                </button>
              </li>
              <li className="sidebar-item">
                <button type="button" className="sidebar-link" onClick={() => navigate('/health-consultation')}>
                  <svg className="sidebar-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Health Consultation
                </button>
              </li>
            </ul>
          </div>

          <div className="sidebar-section">
            <div className="sidebar-title">PDL Management</div>
            <ul className="sidebar-menu">
              <li className="sidebar-item">
                <button type="button" className="sidebar-link" onClick={() => navigate('/final-remarks')}>
                  <svg className="sidebar-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 5H7C6.46957 5 5.96086 5.21071 5.58579 5.58579C5.21071 5.96086 5 6.46957 5 7V19C5 19.5304 5.21071 20.0391 5.58579 20.4142C5.96086 20.7893 6.46957 21 7 21H17C17.5304 21 18.0391 20.7893 18.4142 20.4142C18.7893 20.0391 19 19.5304 19 19V7C19 6.46957 18.7893 5.96086 18.4142 5.58579C18.0391 5.21071 17.5304 5 17 5H15M9 5C9 5.53043 9.21071 6.03914 9.58579 6.41421C9.96086 6.78929 10.4696 7 11 7H13C13.5304 7 14.0391 6.78929 14.4142 6.41421C14.7893 6.03914 15 5.53043 15 5M9 5C9 4.46957 9.21071 3.96086 9.58579 3.58579C9.96086 3.21071 10.4696 3 11 3H13C13.5304 3 14.0391 3.21071 14.4142 3.58579C14.7893 3.96086 15 4.46957 15 5M12 12H15M12 16H15M9 12H9.01M9 16H9.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  PDL Final Remarks
                </button>
              </li>
              <li className="sidebar-item">
                <button type="button" className="sidebar-link" onClick={() => navigate('/pdl-cell-update')}>
                  <svg className="sidebar-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M4 7V17C4 17.5304 4.21071 18.0391 4.58579 18.4142C4.96086 18.7893 5.46957 19 6 19H18C18.5304 19 19.0391 18.7893 19.4142 18.4142C19.7893 18.0391 20 17.5304 20 17V7C20 6.46957 19.7893 5.96086 19.4142 5.58579C19.0391 5.21071 18.5304 5 18 5H6C5.46957 5 4.96086 5.21071 4.58579 5.58579C4.21071 5.96086 4 6.46957 4 7Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M9 5V19M15 5V19M4 12H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M16 9L18 11L16 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  PDL Cell Assignment
                </button>
              </li>
            </ul>
          </div>
          
          <div className="sidebar-section">
            <div className="sidebar-title">Search & Reports</div>
            <ul className="sidebar-menu">
              <li className="sidebar-item">
                <button type="button" className="sidebar-link" onClick={() => navigate('/pdl-health-record-search')}>
                  <svg className="sidebar-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <polyline points="14,2 14,8 20,8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <circle cx="11.5" cy="14.5" r="2.5" stroke="currentColor" strokeWidth="1.5"/>
                    <path d="M13.3 16.3L15 18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                  </svg>
                  Search Health Records
                </button>
              </li>
              <li className="sidebar-item">
                <button type="button" className="sidebar-link" onClick={() => navigate('/pdl-consultation-search')}>
                  <svg className="sidebar-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M16 4H18C18.5304 4 19.0391 4.21071 19.4142 4.58579C19.7893 4.96086 20 5.46957 20 6V20C20 20.5304 19.7893 21.0391 19.4142 21.4142C19.0391 21.7893 18.5304 22 18 22H6C5.46957 22 4.96086 21.7893 4.58579 21.4142C4.21071 21.0391 4 20.5304 4 20V6C4 5.46957 4.21071 4.96086 4.58579 4.58579C4.96086 4.21071 5.46957 4 6 4H8M16 4C16 2.89543 15.1046 2 14 2H10C8.89543 2 8 2.89543 8 4M16 4C16 5.10457 15.1046 6 14 6H10C8.89543 6 8 5.10457 8 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <circle cx="11" cy="14" r="2" stroke="currentColor" strokeWidth="1.5"/>
                    <path d="M12.5 15.5L14 17" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                  </svg>
                  Search Consultations
                </button>
              </li>
              <li className="sidebar-item">
                <button type="button" className="sidebar-link" onClick={() => navigate('/final-remarks-search')}>
                  <svg className="sidebar-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M16 4H18C18.5304 4 19.0391 4.21071 19.4142 4.58579C19.7893 4.96086 20 5.46957 20 6V20C20 20.5304 19.7893 21.0391 19.4142 21.4142C19.0391 21.7893 18.5304 22 18 22H6C5.46957 22 4.96086 21.7893 4.58579 21.4142C4.21071 21.0391 4 20.5304 4 20V6C4 5.46957 4.21071 4.96086 4.58579 4.58579C4.96086 4.21071 5.46957 4 6 4H8M16 4C16 2.89543 15.1046 2 14 2H10C8.89543 2 8 2.89543 8 4M16 4C16 5.10457 15.1046 6 14 6H10C8.89543 6 8 5.10457 8 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <circle cx="11" cy="14" r="2" stroke="currentColor" strokeWidth="1.5"/>
                    <path d="M12.5 15.5L14 17" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                  </svg>
                  Search Final Remarks
                </button>
              </li>
              <li className="sidebar-item">
                <button type="button" className="sidebar-link" onClick={() => navigate('/filter-health')}>
                  <svg className="sidebar-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M22 12h-4l-3 9L9 3l-3 9H2" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                  Filter PDL
                </button>
              </li>
            </ul>
          </div>
        </aside>

        {/* Main Content Area */}
        <main className="main-content">
          {/* Key Performance Indicators */}
          <div className="kpi-grid">
            {loading ? (
              // Skeleton loaders for KPI cards
              <>
                {[1, 2, 3, 4, 5, 6].map((index) => (
                  <div key={index} className="skeleton-kpi-card">
                    <div className="skeleton-kpi-header">
                      <div className="skeleton-icon"></div>
                      <div className="skeleton-trend"></div>
                    </div>
                    <div className="skeleton-value"></div>
                    <div className="skeleton-label"></div>
                    <div className="skeleton-sublabel"></div>
                    <div className="skeleton-chart"></div>
                  </div>
                ))}
              </>
            ) : (
              <>
            <div className="kpi-card secondary" onClick={() => navigate('/pdl-health-record-search', { state: { autoLoad: true } })} style={{cursor: 'pointer'}}>
              <div className="kpi-header">
                <div className="kpi-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2V22M17 7H7M17 17H7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
                  </svg>
                </div>
              </div>
              <div className="kpi-content">
                <div className="kpi-value">
                  {loading ? '...' : 
                    analyticsData.healthRecordsCount?.data?.total || 
                    analyticsData.kpis?.healthRecords?.value || 
                    0
                  }
                </div>
                <div className="kpi-label">Health Records</div>
                <div className="kpi-sublabel">
                  {loading ? 'Loading...' : 
                    analyticsData.healthRecordsCount?.data ? 
                      `Total Health Records (${analyticsData.healthRecordsCount.data.thisMonth} this month)` :
                      'Medical assessments completed'
                  }
                </div>
              </div>
              <div className="kpi-chart">
                <div className="mini-chart">
                  <div className="chart-line" style={{background: 'linear-gradient(90deg, rgba(16,185,129,0.3) 0%, rgba(16,185,129,1) 100%)'}}></div>
                </div>
              </div>
            </div>



            <div className="kpi-card quaternary" onClick={() => navigate('/pdl-consultation-search', { state: { autoLoad: true } })} style={{cursor: 'pointer'}}>
              <div className="kpi-header">
                <div className="kpi-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M16 4H18C18.5304 4 19.0391 4.21071 19.4142 4.58579C19.7893 4.96086 20 5.46957 20 6V20C20 20.5304 19.7893 21.0391 19.4142 21.4142C19.0391 21.7893 18.5304 22 18 22H6C5.46957 22 4.96086 21.7893 4.58579 21.4142C4.21071 21.0391 4 20.5304 4 20V6C4 5.46957 4.21071 4.96086 4.58579 4.58579C4.96086 4.21071 5.46957 4 6 4H8M16 4C16 2.89543 15.1046 2 14 2H10C8.89543 2 8 2.89543 8 4M16 4C16 5.10457 15.1046 6 14 6H10C8.89543 6 8 5.10457 8 4M9 12L11 14L15 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              </div>
              <div className="kpi-content">
                <div className="kpi-value">
                  {loading ? '...' : 
                    analyticsData.dailyConsultationCount?.data?.total || 
                    analyticsData.kpis?.dailyConsultation?.value || 
                    0
                  }
                </div>
                <div className="kpi-label">Consultation</div>
                <div className="kpi-sublabel">
                  {loading ? 'Loading...' : 
                    analyticsData.dailyConsultationCount?.data ? 
                      `Total Consultations (${analyticsData.dailyConsultationCount.data.today} today)` :
                      'Health consultations today'
                  }
                </div>
              </div>
              <div className="kpi-chart">
                <div className="mini-chart">
                  <div className="chart-line" style={{background: 'linear-gradient(90deg, rgba(245,158,11,0.3) 0%, rgba(245,158,11,1) 100%)'}}></div>
                </div>
              </div>
            </div>

            <div className="kpi-card tertiary" onClick={() => navigate('/filter-health', { state: { filterType: 'recommendation', recommendationValue: 'dorm' } })} style={{cursor: 'pointer'}}>
              <div className="kpi-header">
                <div className="kpi-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3 9L12 2L21 9V20C21 20.5304 20.7893 21.0391 20.4142 21.4142C20.0391 21.7893 19.5304 22 19 22H5C4.46957 22 3.96086 21.7893 3.58579 21.4142C3.21071 21.0391 3 20.5304 3 20V9Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <polyline points="9,22 9,12 15,12 15,22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              </div>
              <div className="kpi-content">
                <div className="kpi-value">
                  {loading ? '...' : 
                    analyticsData.generalDormitoryCount?.data?.total || 
                    analyticsData.kpis?.generalDormitory?.value || 
                    0
                  }
                </div>
                <div className="kpi-label">General Dormitory</div>
                <div className="kpi-sublabel">
                  {loading ? 'Loading...' : 
                    analyticsData.generalDormitoryCount?.data ? 
                      `Total dorm recommendations (${analyticsData.generalDormitoryCount.data.thisMonth} this month)` :
                      'Inmates in general housing'
                  }
                </div>
              </div>
              <div className="kpi-chart">
                <div className="mini-chart">
                  <div className="chart-line" style={{background: 'linear-gradient(90deg, rgba(139,92,246,0.3) 0%, rgba(139,92,246,1) 100%)'}}></div>
                </div>
              </div>
            </div>

            <div className="kpi-card quinary" onClick={() => navigate('/filter-health', { state: { filterType: 'recommendation', recommendationValue: 'isolated' } })} style={{cursor: 'pointer'}}>
              <div className="kpi-header">
                <div className="kpi-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M8 12H16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M12 8V16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              </div>
              <div className="kpi-content">
                <div className="kpi-value">
                  {loading ? '...' : 
                    analyticsData.isolationCount?.data?.total || 
                    analyticsData.kpis?.isolation?.value || 
                    0
                  }
                </div>
                <div className="kpi-label">Isolation</div>
                <div className="kpi-sublabel">
                  {loading ? 'Loading...' : 
                    analyticsData.isolationCount?.data ? 
                      `Total isolation recommendations (${analyticsData.isolationCount.data.thisMonth} this month)` :
                      'Inmates in isolation cells'
                  }
                </div>
              </div>
              <div className="kpi-chart">
                <div className="mini-chart">
                  <div className="chart-line" style={{background: 'linear-gradient(90deg, rgba(239,68,68,0.3) 0%, rgba(239,68,68,1) 100%)'}}></div>
                </div>
              </div>
            </div>

            <div className="kpi-card senary" onClick={() => navigate('/filter-health', { state: { filterType: 'recommendation', recommendationValue: 'hospitalized' } })} style={{cursor: 'pointer'}}>
              <div className="kpi-header">
                <div className="kpi-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2V22M17 7H7M17 17H7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
                    <path d="M20 9V15C20 15.5304 19.7893 16.0391 19.4142 16.4142C19.0391 16.7893 18.5304 17 18 17H6C5.46957 17 4.96086 16.7893 4.58579 16.4142C4.21071 16.0391 4 15.5304 4 15V9C4 8.46957 4.21071 7.96086 4.58579 7.58579C4.96086 7.21071 5.46957 7 6 7H18C18.5304 7 19.0391 7.21071 19.4142 7.58579C19.7893 7.96086 20 8.46957 20 9Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              </div>
              <div className="kpi-content">
                <div className="kpi-value">
                  {loading ? '...' : 
                    analyticsData.hospitalCount?.data?.total || 
                    analyticsData.kpis?.hospital?.value || 
                    0
                  }
                </div>
                <div className="kpi-label">Hospital</div>
                <div className="kpi-sublabel">
                  {loading ? 'Loading...' : 
                    analyticsData.hospitalCount?.data ? 
                      `Total hospital recommendations (${analyticsData.hospitalCount.data.thisMonth} this month)` :
                      'Inmates in medical facility'
                  }
                </div>
              </div>
              <div className="kpi-chart">
                <div className="mini-chart">
                  <div className="chart-line" style={{background: 'linear-gradient(90deg, rgba(6,182,212,0.3) 0%, rgba(6,182,212,1) 100%)'}}></div>
                </div>
              </div>
            </div>

            {/* Unassigned PDLs Card */}
            <div className="kpi-card unassigned" onClick={() => navigate('/pdl-cell-update', { state: { autoLoad: true } })} style={{cursor: 'pointer'}}>
              <div className="kpi-header">
                <div className="kpi-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M4 4H20V20H4V4Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M8 8H16V10H8V8Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M8 12H13V14H8V12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M8 16H11V18H8V16Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              </div>
              <div className="kpi-content">
                <div className="kpi-value">
                  {loading ? '...' :
                    analyticsData.unassignedPdlCount?.data?.total || 0
                  }
                </div>
                <div className="kpi-label">Unassigned PDLs</div>
                <div className="kpi-sublabel">
                  {loading ? 'Loading...' :
                    analyticsData.unassignedPdlCount?.data ?
                      `Total PDLs without assigned cell (excluding hospitalized) (${analyticsData.unassignedPdlCount.data.thisMonth} this month)` :
                      'Cell assignment pending'
                  }
                </div>
              </div>
              <div className="kpi-chart">
                <div className="mini-chart">
                  <div className="chart-line" style={{background: 'linear-gradient(90deg, rgba(236,72,153,0.3) 0%, rgba(236,72,153,1) 100%)'}}></div>
                </div>
              </div>
            </div>
              </>
            )}
          </div>

          {/* Analytics Dashboard Grid */}
          <div className="analytics-grid">

            {/* Age Demographics */}
            <div className="analytics-card" onClick={() => navigate('/filter-health', { state: { filterType: 'age' } })} style={{cursor: 'pointer'}}>
              <div className="card-header">
                <h3>Age Distribution</h3>
                <p className="chart-subtitle">Inmate age demographics</p>
              </div>
              <div className="chart-container medium">
                {loading ? (
                  <div className="skeleton-analytics-content">
                    <div className="skeleton-bar-item">
                      <div className="skeleton-bar-label"></div>
                      <div className="skeleton-bar-full"></div>
                    </div>
                    <div className="skeleton-bar-item">
                      <div className="skeleton-bar-label"></div>
                      <div className="skeleton-bar-full"></div>
                    </div>
                    <div className="skeleton-bar-item">
                      <div className="skeleton-bar-label"></div>
                      <div className="skeleton-bar-full"></div>
                    </div>
                    <div className="skeleton-bar-item">
                      <div className="skeleton-bar-label"></div>
                      <div className="skeleton-bar-full"></div>
                    </div>
                  </div>
                ) : (
                <div className="demographics-chart">
                  <div className="age-distribution">
                    <div className="age-bars">
                      {(() => {
                        const allGroups = ['0-17', '18-25', '26-35', '36-45', '46+'];
                        const groupMap = {};
                        (analyticsData.ageDistribution?.ageGroups || []).forEach(g => { groupMap[g.label] = g; });
                        return allGroups.map(label => {
                          const g = groupMap[label] || { label, count: 0, percentage: 0 };
                          const cssKey = label.toLowerCase().replace(/\+/g, '-plus').replace(/[^a-z0-9-]/g, '-');
                          return (
                            <div key={g.label} className="age-bar">
                              <div className="bar-info">
                                <span className="age-label">{g.label}</span>
                                <span className="age-value">{g.count}</span>
                              </div>
                              <div className="bar-container">
                                <div
                                  className={`bar-fill age-${cssKey}`}
                                  style={{width: `${g.percentage}%`}}
                                ></div>
                              </div>
                              <span className="bar-percentage">{g.percentage}%</span>
                            </div>
                          );
                        });
                      })()}
                    </div>
                  </div>
                </div>
                )}
              </div>
            </div>

            {/* Gender Demographics */}
            <div className="analytics-card" onClick={() => navigate('/filter-health', { state: { filterType: 'sex' } })} style={{cursor: 'pointer'}}>
              <div className="card-header">
                <h3>Gender Distribution</h3>
                <p className="chart-subtitle">Male vs Female breakdown</p>
              </div>
              <div className="chart-container medium">
                {loading ? (
                  <div className="skeleton-analytics-content">
                    <div className="skeleton-gender-chart">
                      <div className="skeleton-pie-chart"></div>
                      <div className="skeleton-gender-stats">
                        <div className="skeleton-stat-row"></div>
                        <div className="skeleton-stat-row"></div>
                        <div className="skeleton-stat-row"></div>
                      </div>
                    </div>
                  </div>
                ) : (
                <div className="demographics-chart">
                  <div className="gender-distribution">
                    <div className="gender-visual">
                      <div className="gender-arc-wrapper">
                        <div className="gender-arc">
                          <div 
                            className="arc-segment male" 
                            style={{'--percentage': analyticsData.genderDistribution?.distribution?.male?.percentage ?? 0}}
                          ></div>
                          <div 
                            className="arc-segment female" 
                            style={{'--percentage': analyticsData.genderDistribution?.distribution?.female?.percentage ?? 0}}
                          ></div>
                        </div>
                        <div className="gender-center-legend">
                          <div className="gender-legend-item">
                            <span className="gender-legend-dot male"></span>
                            <span className="gender-legend-label">Male</span>
                          </div>
                          <div className="gender-legend-item">
                            <span className="gender-legend-dot female"></span>
                            <span className="gender-legend-label">Female</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="gender-stats">
                      <div className="stat-item">
                        <span className="stat-label">Total Male</span>
                        <span className="stat-value male">
                          {analyticsData.genderDistribution?.summary?.totalMale ?? analyticsData.genderDistribution?.distribution?.male?.count ?? 0}
                        </span>
                      </div>
                      <div className="stat-item">
                        <span className="stat-label">Total Female</span>
                        <span className="stat-value female">
                          {analyticsData.genderDistribution?.summary?.totalFemale ?? analyticsData.genderDistribution?.distribution?.female?.count ?? 0}
                        </span>
                      </div>
                      <div className="stat-item">
                        <span className="stat-label">Total Inmates</span>
                        <span className="stat-value total">
                          {analyticsData.genderDistribution?.total ?? 0}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                )}
              </div>
            </div>

            {/* Cell Distribution */}
            <div className="analytics-card" onClick={() => navigate('/pdl-cell-update')} style={{cursor: 'pointer'}}>
              <div className="card-header">
                <h3>Cell Distribution</h3>
                <p className="chart-subtitle">Inmate distribution across all cells</p>
              </div>
              <div className="chart-container medium">
                {loading ? (
                  <div className="skeleton-analytics-content">
                    <div className="skeleton-bar-item">
                      <div className="skeleton-bar-label"></div>
                      <div className="skeleton-bar-full"></div>
                    </div>
                    <div className="skeleton-bar-item">
                      <div className="skeleton-bar-label"></div>
                      <div className="skeleton-bar-full"></div>
                    </div>
                    <div className="skeleton-bar-item">
                      <div className="skeleton-bar-label"></div>
                      <div className="skeleton-bar-full"></div>
                    </div>
                  </div>
                ) : (
                  <CellDistributionCard cellData={analyticsData.cellDistribution} />
                )}
              </div>
            </div>

          </div>
        </main>
      </div>

      {/* Change Password Modal */}
      <ChangePasswordModal
        isOpen={showChangePasswordModal}
        onClose={() => setShowChangePasswordModal(false)}
        userData={userData}
        onSuccess={(message) => {
          setSuccessMessage(message || 'Password changed successfully');
          setShowSuccessModal(true);
        }}
        onError={(message) => {
          setErrorMessage(message);
          setShowErrorModal(true);
        }}
      />

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="modal-overlay" onClick={() => setShowSuccessModal(false)}>
          <div className="error-modal" onClick={(e) => e.stopPropagation()} style={{ borderColor: '#22c55e' }}>
            <div className="error-icon" style={{ background: 'rgba(34, 197, 94, 0.1)', borderColor: 'rgba(34, 197, 94, 0.3)', color: '#22c55e' }}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M22 11.08V12C21.9988 14.1564 21.3005 16.2547 20.0093 17.9818C18.7182 19.7088 16.9033 20.9725 14.8354 21.5839C12.7674 22.1953 10.5573 22.1219 8.53447 21.3746C6.51168 20.6273 4.78465 19.2461 3.61096 17.4371C2.43727 15.628 1.87979 13.4881 2.02168 11.3363C2.16356 9.18455 2.99721 7.13631 4.39828 5.49706C5.79935 3.85781 7.69279 2.71537 9.79619 2.24013C11.8996 1.7649 14.1003 1.98232 16.07 2.85999" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M22 4L12 14.01L9 11.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h3 style={{ color: '#22c55e' }}>Success</h3>
            <p>{successMessage}</p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
              <button className="error-btn" onClick={() => setShowSuccessModal(false)} style={{ background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)' }}>OK</button>
            </div>
          </div>
        </div>
      )}

      {/* Error Modal */}
      {showErrorModal && (
        <div className="modal-overlay" onClick={() => setShowErrorModal(false)}>
          <div className="error-modal" onClick={(e) => e.stopPropagation()} style={{ borderColor: '#ef4444' }}>
            <div className="error-icon" style={{ background: 'rgba(239, 68, 68, 0.1)', borderColor: 'rgba(239, 68, 68, 0.3)', color: '#ef4444' }}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" stroke="#ef4444" strokeWidth="2"/>
                <path d="M12 8v4m0 4h.01" stroke="#ef4444" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </div>
            <h3 style={{ color: '#ef4444' }}>Error</h3>
            <p>{errorMessage}</p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
              <button className="error-btn" onClick={() => setShowErrorModal(false)} style={{ background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' }}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Theme Change Confirmation Modal */}
      {showThemeModal && (
        <div className="modal-overlay" onClick={cancelThemeChange}>
          <div className="error-modal" onClick={(e) => e.stopPropagation()} style={{ borderColor: '#f59e0b' }}>
            <div className="error-icon" style={{ background: 'rgba(245, 158, 11, 0.1)', borderColor: 'rgba(245, 158, 11, 0.3)', color: '#f59e0b' }}>
              {pendingTheme ? (
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="currentColor"/>
                </svg>
              ) : (
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 3V4M12 20V21M4 12H3M6.31412 6.31412L5.5 5.5M17.6859 6.31412L18.5 5.5M6.31412 17.69L5.5 18.5M17.6859 17.69L18.5 18.5M21 12H20M16 12C16 14.2091 14.2091 16 12 16C9.79086 16 8 14.2091 8 12C8 9.79086 9.79086 8 12 8C14.2091 8 16 9.79086 16 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )}
            </div>
            <h3 style={{ color: '#f59e0b' }}>Change Theme</h3>
            <p>Are you sure you want to switch to <strong>{pendingTheme ? 'Dark' : 'Light'} Mode</strong>?</p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
              <button className="cancel-btn" onClick={cancelThemeChange}>Cancel</button>
              <button className="error-btn" onClick={confirmThemeChange} style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' }}>Confirm</button>
            </div>
          </div>
        </div>
      )}

      {/* Logout Confirmation Modal */}
      {showLogoutModal && (
        <div className="modal-overlay" onClick={cancelLogout}>
          <div className="error-modal" onClick={(e) => e.stopPropagation()} style={{ borderColor: '#ef4444' }}>
            <div className="error-icon" style={{ background: 'rgba(239, 68, 68, 0.1)', borderColor: 'rgba(239, 68, 68, 0.3)', color: '#ef4444' }}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <polyline points="16,17 21,12 16,7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <line x1="21" y1="12" x2="9" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h3 style={{ color: '#ef4444' }}>Logout</h3>
            <p>Are you sure you want to logout?</p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
              <button className="cancel-btn" onClick={cancelLogout}>Cancel</button>
              <button className="error-btn" onClick={confirmLogout} style={{ background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' }}>Logout</button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}


// Cell Distribution Component with Pagination
function CellDistributionCard({ cellData }) {
  const [currentPage, setCurrentPage] = React.useState(0);
  const ITEMS_PER_PAGE = 4;
  
  if (!cellData?.data?.allCells || cellData.data.allCells.length === 0) {
    return (
      <div className="cell-no-data">
        No cell data available
      </div>
    );
  }

  const allCells = cellData.data.allCells;
  const totalPages = Math.ceil(allCells.length / ITEMS_PER_PAGE) + 1; // +1 for summary page
  const isOnSummaryPage = currentPage === 0;
  
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const currentCells = isOnSummaryPage ? [] : allCells.slice(startIndex, endIndex);

  const handlePrevPage = () => {
    setCurrentPage(prev => Math.max(0, prev - 1));
  };

  const handleNextPage = () => {
    setCurrentPage(prev => Math.min(totalPages - 1, prev + 1));
  };

  return (
    <div className="demographics-chart">
      <div className="age-distribution">
        {isOnSummaryPage ? (
          // Summary page
          cellData.data.summary && (
            <div className="gender-stats cell-summary-stats">
              <div className="stat-item">
                <span className="stat-label">Total Cells</span>
                <span className="stat-value total">
                  {cellData.data.summary.totalCells}
                </span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Occupancy</span>
                <span className="stat-value male">
                  {cellData.data.summary.totalOccupancy}/{cellData.data.summary.totalCapacity}
                </span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Occupancy Rate</span>
                <span className={`stat-value ${cellData.data.summary.occupancyRate >= 90 ? 'female' : 'male'}`}>
                  {cellData.data.summary.occupancyRate}%
                </span>
              </div>
            </div>
          )
        ) : (
          // Cell distribution pages
          <div className="age-bars">
            {currentCells.map((cell) => (
              <div key={cell.id} className="age-bar">
                <div className="bar-info">
                  <span className="age-label">{cell.type} - {cell.cellNo}</span>
                  <span className="age-value">{cell.occupancy}/{cell.capacity}</span>
                </div>
                <div className="bar-container">
                  <div 
                    className={`bar-fill ${
                      cell.status === 'full' ? 'age-46-plus' : 
                      cell.status === 'near_full' ? 'age-36-45' : 
                      'age-18-25'
                    }`}
                    style={{width: `${Math.max(cell.occupancyRate, 2)}%`}}
                  ></div>
                </div>
                <span className="bar-percentage">{cell.occupancyRate}%</span>
              </div>
            ))}
          </div>
        )}
        {totalPages > 1 && (
          <div className="cell-pagination">
            <button
              onClick={(e) => { e.stopPropagation(); handlePrevPage(); }}
              disabled={currentPage === 0}
              className="cell-pagination-btn"
            >
              Previous
            </button>
            <span className="cell-pagination-info">
              Page {currentPage + 1} of {totalPages}
            </span>
            <button
              onClick={(e) => { e.stopPropagation(); handleNextPage(); }}
              disabled={currentPage === totalPages - 1}
              className="cell-pagination-btn"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function App() {
  return (
    <Router>
      <Routes>
        {/* Public Routes - No authentication required */}
        <Route path="/login" element={<Login />} />
        <Route path="/login-admin" element={<LoginAdmin />} />
        <Route path="/login-superuser" element={<LoginSuperuser />} />
        <Route path="/login-jailofficers" element={<LoginJO />} />

        {/* Superuser ONLY Routes - Only superusers can access */}
        <Route 
          path="/su-homepage" 
          element={
            <ProtectedRoute requiredRole="superuser" redirectTo="/login-superuser">
              <SuHomepage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/su-jo-search-consultation" 
          element={
            <ProtectedRoute requiredRole={["superuser","jail_officer"]} redirectTo="/login-superuser">
              <SuJoConsultationSearch />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/su-jo-search-health-record" 
          element={
            <ProtectedRoute requiredRole={["superuser","jail_officer"]} redirectTo="/login-superuser">
              <SuJoHealthRecordSearch />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/su-activity-logs" 
          element={
            <ProtectedRoute requiredRole="superuser" redirectTo="/login-superuser">
              <SuActivityLogs />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/su-pdl-cell" 
          element={
            <ProtectedRoute requiredRole="superuser" redirectTo="/login-superuser">
              <JoPdlCell />
            </ProtectedRoute>
          } 
        />

        {/* Admin ONLY Routes - Only admins can access */}
        <Route 
          path="/sa-homepage" 
          element={
            <ProtectedRoute requiredRole="admin" redirectTo="/login-admin">
              <SaHomepage />
            </ProtectedRoute>
          } 
        />

        {/* Jail Officer ONLY Routes - Only jail officers can access */}
        <Route 
          path="/jo-homepage" 
          element={
            <ProtectedRoute requiredRole="jail_officer" redirectTo="/login-jailofficers">
              <JoHomepage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/jo-search-pdl" 
          element={
            <ProtectedRoute requiredRole="jail_officer" redirectTo="/login-jailofficers">
              <JoSearchPdl />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/jo-pdl-cell" 
          element={
            <ProtectedRoute requiredRole="jail_officer" redirectTo="/login-jailofficers">
              <JoPdlCell />
            </ProtectedRoute>
          } 
        />

        {/* Regular User Routes - Requires any authentication, but NOT superuser or admin */}
        <Route 
          path="/" 
          element={
            <ProtectedRoute redirectTo="/login">
              <FrontPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/dashboard" 
          element={
            <ProtectedRoute redirectTo="/login">
              <FrontPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/pdl-health-record" 
          element={
            <ProtectedRoute redirectTo="/login">
              <PDLHealthRecord />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/pdl-health-wizard" 
          element={
            <ProtectedRoute redirectTo="/login">
              <HRWizard />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/pdl-health-record-search" 
          element={
            <ProtectedRoute redirectTo="/login">
              <PdlHealthRecordSearch />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/pdl-consultation-search" 
          element={
            <ProtectedRoute redirectTo="/login">
              <PdlConsultationSearch />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/filter-health" 
          element={
            <ProtectedRoute redirectTo="/login">
              <FilterPdl />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/su-filter-health" 
          element={
            <ProtectedRoute requiredRole="superuser" redirectTo="/login-superuser">
              <FilterPdl />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/health-consultation" 
          element={
            <ProtectedRoute redirectTo="/login">
              <ConsultWizard />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/consultation-search" 
          element={
            <ProtectedRoute redirectTo="/login">
              <ConsultSearch />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/final-remarks" 
          element={
            <ProtectedRoute redirectTo="/login">
              <FinalRemarks />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/final-remarks-search" 
          element={
            <ProtectedRoute redirectTo="/login">
              <FinalRemarksSearch />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/su-final-remarks-search" 
          element={
            <ProtectedRoute requiredRole="superuser" redirectTo="/login-superuser">
              <FinalRemarksSearch />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/pdl-cell-update" 
          element={
            <ProtectedRoute redirectTo="/login">
              <PdlCellUpdate />
            </ProtectedRoute>
          } 
        />
      </Routes>
    </Router>
  );
}

export default App;
